/**
 * Cookie Consent Utility
 * Manages user consent preferences for cookies (GDPR/CCPA compliant)
 */

export type ConsentCategory = 'essential' | 'analytics' | 'marketing';

export interface ConsentPreferences {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
  timestamp: number;
  version: string;
}

export const CONSENT_CONFIG = {
  cookieName: 'peetrack-consent',
  expires: 365, // 1 year
  version: '1.0',
  storageKey: 'peetrack-consent-prefs',
};

/**
 * Check if user has consented to a specific category
 */
export function hasUserConsent(category: ConsentCategory): boolean {
  // Essential cookies are always allowed
  if (category === 'essential') {
    return true;
  }

  // Check if running in browser
  if (typeof window === 'undefined') {
    return false;
  }

  // Check localStorage for stored preferences
  try {
    const stored = localStorage.getItem(CONSENT_CONFIG.storageKey);
    if (!stored) {
      return false;
    }

    const prefs: ConsentPreferences = JSON.parse(stored);
    return prefs[category] || false;
  } catch (error) {
    console.error('Error reading consent preferences:', error);
    return false;
  }
}

/**
 * Check if analytics should be loaded
 */
export function shouldLoadAnalytics(): boolean {
  return hasUserConsent('analytics');
}

/**
 * Check if marketing cookies should be loaded
 */
export function shouldLoadMarketing(): boolean {
  return hasUserConsent('marketing');
}

/**
 * Check if user has Do Not Track enabled
 */
export function respectDNT(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }

  return (
    navigator.doNotTrack === '1' ||
    (window as any).doNotTrack === '1' ||
    (navigator as any).msDoNotTrack === '1'
  );
}

/**
 * Store consent preferences
 */
export function storeConsentPreferences(prefs: Partial<ConsentPreferences>): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const currentPrefs = getConsentPreferences();
    const newPrefs: ConsentPreferences = {
      ...currentPrefs,
      ...prefs,
      essential: true, // Always true
      timestamp: Date.now(),
      version: CONSENT_CONFIG.version,
    };

    localStorage.setItem(CONSENT_CONFIG.storageKey, JSON.stringify(newPrefs));
  } catch (error) {
    console.error('Error storing consent preferences:', error);
  }
}

/**
 * Get current consent preferences
 */
export function getConsentPreferences(): ConsentPreferences {
  if (typeof window === 'undefined') {
    // Default preferences for SSR/build
    return {
      essential: true,
      analytics: false,
      marketing: false,
      timestamp: 0,
      version: CONSENT_CONFIG.version,
    };
  }

  try {
    const stored = localStorage.getItem(CONSENT_CONFIG.storageKey);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('Error getting consent preferences:', error);
  }

  // Default preferences (nothing consented)
  return {
    essential: true,
    analytics: false,
    marketing: false,
    timestamp: 0,
    version: CONSENT_CONFIG.version,
  };
}

/**
 * Reset consent preferences (clear stored data)
 */
export function resetConsent(): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    localStorage.removeItem(CONSENT_CONFIG.storageKey);
  } catch (error) {
    console.error('Error resetting consent:', error);
  }
}

/**
 * Check if user has already made a consent decision
 */
export function hasConsentDecision(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    const stored = localStorage.getItem(CONSENT_CONFIG.storageKey);
    return !!stored;
  } catch (error) {
    return false;
  }
}
